<template>
  <div class="dropdown-wrapper">
    <DxDropDownBox
      v-model="selectedValue"
      :data-source="items"
      display-expr="name"
      value-expr="id"
      placeholder="Select an item..."
      :show-clear-button="false"
      :elementAttr="{ class: 'gc-reorderable-dropdown' }"
      :dropDownOptions="{
        width: 350,
        wrapperAttr: {
          class: 'gc-reorderable-dropdown-popup',
        },
      }"
      :width="160"
      :searchEnabled="true"
      @initialized="onInitialized"
    >
      <template #content>
        <div class="dropdown-content">
          <draggable
            v-model="items"
            handle=".drag-handle"
            animation="200"
            ghost-class="drag-ghost"
            @end="onReorder"
          >
            <div
              v-for="item in items"
              :key="item.id"
              class="dropdown-item"
              :class="{ selected: selectedValue === item.id }"
              @click="selectItem($event, item)"
              @mouseover="onDropDownItemHover($event, item)"
              @mouseleave="onDropDownItemLeave($event, item)"
            >
              <!-- Drag handle -->
              <!-- Disabled for now as dragging and reordering will be handdle in a diffrent user story. -->
              <!-- <span class="drag-handle" title="Drag to reorder" @click.stop>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="currentColor"
                >
                  <circle cx="4" cy="3" r="1.2" />
                  <circle cx="4" cy="7" r="1.2" />
                  <circle cx="4" cy="11" r="1.2" />
                  <circle cx="9" cy="3" r="1.2" />
                  <circle cx="9" cy="7" r="1.2" />
                  <circle cx="9" cy="11" r="1.2" />
                </svg>
              </span> -->

              <!-- Item label -->
              <span class="item-label">{{ item.name }}</span>

              <dx-tooltip :ref="tooltipItemNameRef + item.id" position="right">
                <div class="tooltipContent">
                  <div>
                    {{ item.name }}
                  </div>
                </div>
              </dx-tooltip>

              <span
                class="dx-icon fa-solid fa-users"
                v-if="
                  item.original && item.original.TemplateType === 'Everyone'
                "
              ></span>
              <span class="dx-icon fa-solid fa-user" v-else></span>

              <!-- Optional badge -->
              <span
                v-if="item.badge"
                class="item-badge"
                :class="item.badgeType"
              >
                {{ item.badge }}
              </span>

              <!-- Ellipsis button -->
              <button
                class="ellipsis-btn"
                :class="{ active: activeMenu === item.id }"
                @click.stop="toggleMenu(item.id, $event)"
                title="More options"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <circle cx="3" cy="8" r="1.5" />
                  <circle cx="8" cy="8" r="1.5" />
                  <circle cx="13" cy="8" r="1.5" />
                </svg>
              </button>
            </div>
          </draggable>
        </div>
      </template>
    </DxDropDownBox>

    <dx-tooltip :ref="tooltipSelectedRef" position="right">
      <div class="tooltipContent">
        <div>
          {{
            getSelectedValueData() ? getSelectedValueData().TemplateName : ""
          }}
        </div>
      </div>
    </dx-tooltip>

    <!-- Context Menu (portal-style, appended to body via fixed positioning) -->
    <transition name="menu-fade">
      <div
        v-if="activeMenu !== null"
        class="context-menu"
        :style="menuStyle"
        ref="contextMenu"
      >
        <button
          v-for="action in menuActions"
          :key="action.key"
          class="context-menu-item"
          :class="{ danger: action.danger }"
          @click="handleAction(action.key)"
          :disabled="isActionDisabled(action)"
        >
          <!-- We can add icons here if needed in the future, for now we will keep it
          simple with just text. -->
          <!-- <span class="action-icon">{{ action.icon }}</span> -->
          {{ action.label }}
        </button>
      </div>
    </transition>

    <gcPopupRenameLayout
      :visible.sync="getVisibleRenameSavePopup"
      :definitionKey="definitionKey"
      :showLoading="showLoading"
      ref="renameSavePopup"
      v-if="getVisibleRenameSavePopup"
    />
  </div>
</template>

<script>
import { DxDropDownBox } from "devextreme-vue/drop-down-box";
import draggable from "vuedraggable";
import { mapGetters } from "vuex";
import { secureStorage } from "../../_services/storage";
import ReportService from "../../_services/api/templates/report.service";
import gcPopupRenameLayout from "../../popups/data-grid/gc-popup-rename-layout.vue";
import DxTooltip from "devextreme-vue/tooltip";
import { on } from "devextreme/events";

export default {
  name: "ReorderableDropdown",
  components: { DxDropDownBox, draggable, gcPopupRenameLayout, DxTooltip },
  props: {
    gridDefinitions: {
      type: Array,
      default: () => [],
    },
    definitionKey: {
      type: String,
      required: true,
    },
    showLoading: {
      type: Function,
      default: () => {}, // Default to an empty function to avoid errors if not provided
    },
  },

  data() {
    return {
      selectedValue: null,
      activeMenu: null,
      menuStyle: {},
      // Derive from prop, keep local copy for reordering
      items: this.gridDefinitions.map((d) => ({
        id: d.RecordID,
        name: d.TemplateName,
        badge: null,
        original: d, // keep reference to full definition object
      })),
      dropDownInstance: null,
      visibleRenameSavePopup: false,
      tooltipItemNameRef: "tooltipItemNameRef",
      tooltipSelectedRef: "tooltipSelectedRef",
    };
  },

  mounted() {
    if (this.getSelectedDefinitionByKey(this.definitionKey)) {
      this.selectedValue =
        this.getSelectedDefinitionByKey(this.definitionKey).RecordID || null;
    }
    document.addEventListener("keydown", this.onEsc);
    document.addEventListener("click", this.onClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEsc);
    document.removeEventListener("click", this.onClickOutside);
  },

  computed: {
    /*
      * Get grid view definitions from Vuex store based on the provided definitionKey prop
      @returns {Array} - An array of grid view definitions associated with the definitionKey
      */
    ...mapGetters({
      getGridViewDefinitionByKey:
        "gridViewDefinition/getGridViewDefinitionByKey",
    }),
    /*
      * Get the currently selected grid view definition from Vuex store based on the provided definitionKey prop
      @returns {Object|null} - The selected grid view definition associated with the definitionKey, or null if none is selected
      */
    ...mapGetters({
      getSelectedDefinitionByKey:
        "gridViewDefinition/getSelectedDefinitionByKey",
    }),
    /*
      * Get the change require flag from Vuex store to determine if the grid view definitions have unsaved changes
      @returns {Boolean} - True if there are unsaved changes to the grid view definitions, false otherwise
      */
    ...mapGetters({
      getChangeRequire: "gridViewDefinition/getChangeRequire",
    }),
    /*
      * Get the grid view definitions for the current definitionKey
      @returns {Array} - An array of grid view definitions associated with the definitionKey
      */
    getGridDefinitions() {
      const gridDefinitions = this.getGridViewDefinitionByKey(
        this.definitionKey,
      );

      return gridDefinitions;
    },
    /*
      * Determine if the current user is an admin based on secure storage
      @returns {Boolean} - True if the user is an admin, false otherwise
      */
    isAdminUser() {
      return secureStorage.get("isAdmin");
    },
    /*
      * Define the actions available in the context menu for each dropdown item, along with their labels and disabled states based on user permissions
      @returns {Array} - An array of action objects for the context menu
      */
    menuActions() {
      return [
        { key: "rename", label: "Rename View" },
        {
          key: "duplicate",
          label: "Duplicate View",
        },
        { key: "delete", label: "Delete View" },
      ];
    },
    /*
     * Computed property to get and set the visibility of the popup
     */
    getVisibleRenameSavePopup: {
      get() {
        return this.visibleRenameSavePopup;
      },
      set(value) {
        this.visibleRenameSavePopup = value;
      },
    },
  },

  methods: {
    /*
     * Show tooltip on dropdown item hover
     @param {Event} e - Mouse event
     @param {Object} item - The dropdown item being hovered
     */
    onDropDownItemHover(e, item) {
      const tooltipRef = this.tooltipItemNameRef + item.id;

      if (
        this.$refs[tooltipRef] &&
        this.$refs[tooltipRef][0] &&
        this.$refs[tooltipRef][0].instance
      ) {
        this.$refs[tooltipRef][0].instance.show(e.target);
      }
    },
    /*
      * Hide tooltip on dropdown item mouse leave
      @param {Event} e - Mouse event
      @param {Object} item - The dropdown item being left
      */
    onDropDownItemLeave(e, item) {
      const tooltipRef = this.tooltipItemNameRef + item.id;

      if (
        this.$refs[tooltipRef] &&
        this.$refs[tooltipRef][0] &&
        this.$refs[tooltipRef][0].instance
      ) {
        this.$refs[tooltipRef][0].instance.hide();
      }
    },
    /*
     * Handle clicks outside the context menu to close it
     @param {Event} e - Click event
     */
    onClickOutside(e) {
      if (!this.activeMenu) return;

      const menu = this.$refs.contextMenu;
      if (menu && !menu.contains(e.target)) {
        this.closeMenu();
      }
    },
    /*
     * Handle DxDropDownBox initialization
     @param {Event} e - Initialization event
     */
    onInitialized(e) {
      this.dropDownInstance = e.component; // 👈 store the DxDropDownBox instance

      var self = this;
      on(e.element, "mouseover", (arg) => {
        self.$refs[self.tooltipSelectedRef].instance.show(arg.target);
      });

      on(e.element, "mouseout", () => {
        self.$refs[self.tooltipSelectedRef].instance.hide();
      });
    },
    /*
     * Handle item selection
     @param {Event} e - Selection event
     @param {Object} item - The selected item
     */
    selectItem(e, item) {
      this.selectedValue = item.id;

      if (item.original) {
        this.$store.dispatch("gridViewDefinition/setSelectedDefinition", {
          key: this.definitionKey,
          viewDefinition: item.original,
        });

        this.$store.dispatch(
          "gridViewDefinition/setTriggerViewDefinitionChange",
          true,
        );
      }

      // if an event is passed, it means the click came from the dropdown item, so we should close the menu
      if (e) {
        // close the DxDropDownBox popup
        if (this.dropDownInstance) {
          this.dropDownInstance.close();
        }
      }
    },
    /*
     * Toggle the context menu for a given item
     @param {Number} itemId - The ID of the item for which to toggle the menu
     @param {Event} event - The click event that triggered the toggle
     */
    toggleMenu(itemId, event) {
      if (this.activeMenu === itemId) {
        this.closeMenu();
        return;
      }
      this.activeMenu = itemId;
      this.$nextTick(() => {
        const btn = event.currentTarget;
        const rect = btn.getBoundingClientRect();
        const menuWidth = 180;
        let left = rect.right - menuWidth;
        let top = rect.bottom + 4;
        // Keep within viewport
        if (left < 8) left = 8;
        if (top + 220 > window.innerHeight) top = rect.top - 220;
        this.menuStyle = { top: `${top}px`, left: `${left}px` };
      });
    },
    /*
     * Close the context menu
     */
    closeMenu() {
      this.activeMenu = null;
    },
    /*
      * Handle Escape key to close the context menu
      @param {Event} e - Keydown event
      */
    onEsc(e) {
      if (e.key === "Escape") this.closeMenu();
    },
    /*
     * Determine if a context menu action should be disabled based on the selected item and user permissions
     @param {Object} action - The action being evaluated (e.g., rename, duplicate, delete)
     @returns {Boolean} - True if the action should be disabled, false otherwise
     */
    isActionDisabled(action) {
      const id = this.activeMenu;
      const idx = this.items.findIndex((i) => i.id === id);
      if (idx === -1) return;

      if (this.items[idx]) {
        const isEveryone = this.items[idx].original.TemplateType === "Everyone";
        const isMe = this.items[idx].original.TemplateType === "Me";
        const isDefault = this.items[idx].original.IsDefault === "Y";

        if (action.key === "delete") {
          if (isDefault) return true; // Default view cannot be deleted
          if (isMe) return false; // Users can delete their own (Me) views, but not Everyone views
          if (isEveryone) return !this.isAdminUser; // Only Admin users can delete Everyone views
        }

        // User should be able to rename their own  (Me) views and only Admin users are able to Rename all
        if (action.key === "rename") {
          if (isMe) return false; // Users can rename their own (Me) views
          if (isEveryone) return !this.isAdminUser; // Only Admin users can rename Everyone views
        }

        // only Admin users can duplicate views
        if (action.key === "duplicate") {
          return !this.isAdminUser; // Only Admin users can duplicate views
        }
      }

      return false;
    },
    /*
     * Handle context menu actions (rename, duplicate, delete)
     @param {String} key - The key of the action to handle
     */
    handleAction(key) {
      const id = this.activeMenu;
      const idx = this.items.findIndex((i) => i.id === id);
      if (idx === -1) return;

      switch (key) {
        case "rename":
          this.renameViewDefinition(this.items[idx].original);
          break;
        case "duplicate": {
          const clone = { ...this.items[idx], id: Date.now() };
          this.duplicateViewDefinition(clone.original);
          break;
        }
        case "delete":
          this.deleteViewDefinition(this.items[idx].original);
          break;
      }
      this.closeMenu();
    },
    /*
     * Open the rename popup and initialize it with the selected view definition
     @param {Object} definition - The view definition to rename
     */
    renameViewDefinition(definition) {
      this.getVisibleRenameSavePopup = true;

      this.$nextTick(() => {
        const popup = this.$refs["renameSavePopup"];
        if (popup) {
          const formComponent = popup.$refs["gc-header-form"];
          if (formComponent) {
            formComponent.updateFieldValue("viewName", definition.TemplateName);
            popup.updateModel("variantId", definition.RecordID);
          }
        }
      });
    },
    /*
     * Duplicate a view definition
     @param {Object} definition - The view definition to duplicate
     */
    async duplicateViewDefinition(definition) {
      const confirm = await this.$confirm(
        "Are you sure you want to duplicate this view definition?",
        "Confirm Duplication",
        {
          confirmButtonText: "Yes, Duplicate",
          cancelButtonText: "Cancel",
          type: "warning",
        },
      );

      if (!confirm) return;

      const selectedDef = this.getSelectedDefinitionByKey(this.definitionKey);
      this.showLoading(true);

      try {
        const saveResponse = await ReportService.duplicateViewVariant(
          definition.RecordID,
        );

        const data = saveResponse.data;
        if (data) {
          this.$store.dispatch("gridViewDefinition/updateGridViewDefinition", {
            key: this.definitionKey,
            viewDefinition: data,
          });

          this.$nextTick(() => {
            this.$store.dispatch("gridViewDefinition/setSelectedDefinition", {
              key: this.definitionKey,
              viewDefinition: data,
            });

            setTimeout(() => {
              if (selectedDef && selectedDef.RecordID !== definition.RecordID) {
                this.$store.dispatch(
                  "gridViewDefinition/setTriggerViewDefinitionChange",
                  true,
                );
              }
            }, 10);
          });
        }
      } catch (error) {
        this.$store.dispatch("ui/setServerError", {
          error: error,
          title: "Failed",
        });
      } finally {
        this.showLoading(false);
      }
    },

    /*
      * Delete a view definition  
      @param {Object} definition - The view definition to delete
      */
    async deleteViewDefinition(definition) {
      const confirm = await this.$confirm(
        "Are you sure you want to delete this view definition?",
        "Confirm Deletion",
      );

      if (!confirm) return;

      this.showLoading(true);
      try {
        const response = await ReportService.deleteViewVariant(
          definition.RecordID,
        );

        if (response) {
          this.$notify({
            type: "success",
            message: "View definition deleted successfully.",
          });

          this.$store.dispatch("gridViewDefinition/updateDeleteDefinition", {
            key: definition.GridKey,
            viewDefinition: definition,
          });
        }
      } catch (error) {
        this.$store.dispatch("ui/setServerError", {
          error: error,
          title: "Failed",
        });
      } finally {
        this.showLoading(false);
      }
    },

    /*
     * Handle reordering of items in the dropdown
     */
    onReorder() {
      // Items array is already updated by vuedraggable v-model

      this.$emit(
        "reorder",
        this.items.map((i) => i.original),
      );
    },
    /*
     * Get the currently selected value (RecordID)
     @returns {Number|null} - The RecordID of the selected view definition, or null if none is selected
     */
    getSelectedValue() {
      return this.selectedValue;
    },

    /*
     * Get the data of the currently selected value
     @returns {Object|null} - The data of the selected view definition, or null if none is selected
     */
    getSelectedValueData() {
      const selected = this.items.find((i) => i.id === this.selectedValue);
      return selected ? selected.original : null;
    },
  },
  // ADD watcher to sync if parent gridDefinitions prop changes:
  watch: {
    gridDefinitions(newDefs) {
      this.items = newDefs.map((d) => ({
        id: d.RecordID,
        name: d.TemplateName,
        badge: null,
        original: d,
      }));
    },
    getChangeRequire: {
      handler(value) {
        if (value) {
          this.items = this.getGridViewDefinitionByKey(this.definitionKey).map(
            (d) => ({
              id: d.RecordID,
              name: d.TemplateName,
              badge: null,
              original: d,
            }),
          );

          this.selectedValue = this.getSelectedDefinitionByKey(
            this.definitionKey,
          )
            ? this.getSelectedDefinitionByKey(this.definitionKey).RecordID
            : null;
        }
      },
      deep: true,
    },

    getGridDefinitions: {
      handler(newDefs) {
        this.items = (newDefs || []).map((d) => ({
          id: d.RecordID,
          name: d.TemplateName,
          badge: null,
          original: d,
        }));
      },
      deep: true,
      immediate: true, // ✅ also replaces the manual items init in data()
    },
  },
};
</script>

<style lang="scss">
.gc-reorderable-dropdown-popup {
  .dx-popup-content {
    padding: unset !important;
  }
}

.dropdown-wrapper {
  position: relative;
  // font-family: "Segoe UI", system-ui, sans-serif;
}

/* ── Dropdown content panel ── */
.dropdown-content {
  padding: 6px 0;
  min-width: 280px;
  background: #fff;
  border-radius: 6px;
}

/* ── Each row ── */
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  cursor: pointer;
  // border-radius: 4px;
  // margin: 0 4px;
  transition: background 0.12s;
  user-select: none;

  .dx-icon {
    font-size: 12px;
  }
  .dx-icon::before {
    font-size: 12px;
    color: rgba(148, 148, 148, 0.87);
  }
}
.dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.87);
}
.dropdown-item.selected {
  background: rgba(0, 0, 0, 0.12);
  // font-weight: 500;
  color: #000;
}

/* ── Drag handle ── */
.drag-handle {
  color: #bfc5d1;
  cursor: grab;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
  border-radius: 3px;
  transition: color 0.15s;
}
.drag-handle:hover {
  color: #7a8bab;
  background: #eef0f4;
}
.drag-handle:active {
  cursor: grabbing;
}

/* ── Ghost (drag preview) ── */
.drag-ghost {
  opacity: 0.45;
  background: #dbe4ff;
  border-radius: 4px;
}

/* ── Label ── */
.item-label {
  flex: 1;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Badge ── */
.item-badge {
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  flex-shrink: 0;
}
.badge-green {
  background: #d4f7e7;
  color: #1a7a4a;
}
.badge-blue {
  background: #dbeafe;
  color: #1d55c4;
}
.badge-red {
  background: #fde8e8;
  color: #c0392b;
}

/* ── Ellipsis button ── */
.ellipsis-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #9aa4b8;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.15s, background 0.15s, color 0.15s;
  padding: 0;
}
.dropdown-item:hover .ellipsis-btn,
.ellipsis-btn.active {
  opacity: 1;
}
.ellipsis-btn:hover,
.ellipsis-btn.active {
  background: #e2e8f4;
  color: #3a4fd4;
}

/* ── Add row ── */
.add-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  margin: 2px 4px 0;
  color: #7a8bab;
  font-size: 13px;
  cursor: pointer;
  border-radius: 4px;
  border-top: 1px solid #f0f2f7;
  margin-top: 4px;
  transition: background 0.12s, color 0.12s;
}
.add-item-row:hover {
  background: #f0f4ff;
  color: #3a4fd4;
}
.add-icon {
  font-size: 15px;
  font-weight: 300;
  line-height: 1;
}

/* ── Context menu ── */
.context-menu {
  position: fixed;
  z-index: 9999;
  background: #ffffff;
  border: 1px solid #e2e6f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 4px;
  min-width: 180px;
}
.context-menu-item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 12px;
  font-size: 13px;
  background: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  color: #2d3748;
  text-align: left;
  transition: background 0.1s;
}

.context-menu {
  button[disabled="disabled"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.context-menu-item:hover {
  background: #f4f7ff;
}
.context-menu-item.danger {
  color: #c0392b;
}
.context-menu-item.danger:hover {
  background: #fef2f2;
}
.action-icon {
  font-size: 14px;
  width: 18px;
  text-align: center;
}

/* ── Click-outside overlay ── */
.menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
}

/* ── Transition ── */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.12s, transform 0.12s;
}
.menu-fade-enter,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.97);
}
</style>
